import { safeIntlSupported } from "@multisystemsuite/timezone-engine-utils";

const DISPLAY_SUFFIX_PATTERN = /\s*\(.*\)\s*$/;

/**
 * Strip any display suffix from a timezone string and return a bare IANA id.
 *
 * @example
 * normalizeTimezone("America/New_York (GMT-5:00)") // "America/New_York"
 * normalizeTimezone("Asia/Kolkata (GMT+5:30)")     // "Asia/Kolkata"
 * normalizeTimezone("")                            // "UTC"
 */
export function normalizeTimezone(timeZone: string | null | undefined): string {
  if (!timeZone) return "UTC";
  const stripped = timeZone.replace(DISPLAY_SUFFIX_PATTERN, "").trim();
  return stripped || "UTC";
}

/** Validate a bare IANA timezone id using the runtime Intl implementation */
export function isValidIanaTimezone(timeZone: string): boolean {
  if (!safeIntlSupported()) return true;
  try {
    Intl.DateTimeFormat(undefined, { timeZone });
    return true;
  } catch {
    return false;
  }
}

/**
 * Normalise and validate in one step. Returns the IANA id if valid,
 * otherwise returns the fallback and logs a warning.
 */
export function safeTimezone(timeZone: string | null | undefined, fallback = "UTC"): string {
  const tz = normalizeTimezone(timeZone);
  if (isValidIanaTimezone(tz)) return tz;
  console.warn(
    `[timezone-engine] Invalid timezone "${String(timeZone)}" (normalised: "${tz}"), falling back to ${fallback}`,
  );
  return fallback;
}

/**
 * Sanitise a timezone value before saving it to the database.
 * Strips any display suffix so only the bare IANA id is persisted.
 *
 * @example
 * site.TimeZone = sanitizeTimezoneForDB(req.body.TimeZone);
 */
export function sanitizeTimezoneForDB(timeZone: string | null | undefined): string {
  return safeTimezone(timeZone);
}
